/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
*/

import React, { useState, useEffect } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { approvalService, ApprovalRequest } from '../../services/registryApi';
import toast from 'react-hot-toast';

const stageBadge = (stage: string) => {
  const colors: Record<string, string> = {
    production: 'bg-green-500/20 text-green-400',
    staging: 'bg-yellow-500/20 text-yellow-400',
    development: 'bg-blue-500/20 text-blue-400',
    archived: 'bg-gray-700 text-gray-400',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${colors[stage] || 'bg-gray-700 text-gray-400'}`}>
      {stage}
    </span>
  );
};

const statusBadge = (status: string) => {
  const config: Record<string, { cls: string; label: string }> = {
    pending: { cls: 'bg-yellow-500/20 text-yellow-400', label: 'Pending' },
    approved: { cls: 'bg-green-500/20 text-green-400', label: 'Approved' },
    rejected: { cls: 'bg-red-500/20 text-red-400', label: 'Rejected' },
    superseded: { cls: 'bg-gray-700 text-gray-400', label: 'Superseded' },
  };
  const { cls, label } = config[status] || config.pending;
  return <span className={`px-2 py-0.5 text-xs font-medium rounded ${cls}`}>{label}</span>;
};

const Approvals: React.FC = () => {
  const [approvals, setApprovals] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [reviewComment, setReviewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | null>(null);

  useEffect(() => {
    const fetchApprovals = async () => {
      setLoading(true);
      try {
        const res = await approvalService.listPending();
        setApprovals(res.data.approvals || []);
      } catch {
        toast.error('Failed to load approval requests');
      } finally {
        setLoading(false);
      }
    };
    fetchApprovals();
  }, []);

  const openReview = (id: string, action: 'approve' | 'reject') => {
    setReviewingId(id);
    setReviewAction(action);
    setReviewComment('');
  };

  const handleSubmitReview = async () => {
    if (!reviewingId || !reviewAction) return;
    setSubmitting(true);
    try {
      if (reviewAction === 'approve') {
        await approvalService.approve(reviewingId, reviewComment || undefined);
        toast.success('Promotion approved');
      } else {
        await approvalService.reject(reviewingId, reviewComment || undefined);
        toast.success('Promotion rejected');
      }
      setApprovals((prev) => prev.filter((a) => a.id !== reviewingId));
      setReviewingId(null);
    } catch {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <ShieldCheckIcon className="w-8 h-8 text-primary-500" />
            <h1 className="text-2xl font-bold text-white">Model Approval Queue</h1>
          </div>
          <p className="text-gray-400 mt-1">
            Review and approve model promotions to Staging and Production.
          </p>
        </div>

        {/* Review Dialog */}
        {reviewingId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-white mb-4">
                {reviewAction === 'approve' ? 'Approve Promotion' : 'Reject Promotion'}
              </h3>
              <p className="text-sm text-gray-400 mb-4">
                {reviewAction === 'approve'
                  ? 'Add an optional comment explaining your approval decision.'
                  : 'Explain why this promotion is being rejected (optional).'}
              </p>
              <textarea
                className="w-full h-28 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                placeholder="Add a review comment..."
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
              />
              <div className="flex gap-3 mt-4">
                <button
                  onClick={handleSubmitReview}
                  disabled={submitting}
                  className={`flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg disabled:opacity-50 ${
                    reviewAction === 'approve'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {submitting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : reviewAction === 'approve' ? (
                    <CheckCircleIcon className="w-4 h-4" />
                  ) : (
                    <XCircleIcon className="w-4 h-4" />
                  )}
                  {reviewAction === 'approve' ? 'Approve' : 'Reject'}
                </button>
                <button
                  onClick={() => setReviewingId(null)}
                  className="px-4 py-2 text-gray-400 hover:text-white text-sm font-medium rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
          </div>
        ) : approvals.length === 0 ? (
          <div className="bg-gray-900 rounded-xl border border-gray-700 p-16 text-center">
            <CheckCircleIcon className="mx-auto h-12 w-12 text-gray-600 mb-3" />
            <h3 className="text-sm font-medium text-white mb-1">No pending approvals</h3>
            <p className="text-sm text-gray-400">
              All promotion requests have been reviewed. New requests appear here when a team member
              promotes a model version to Staging or Production.
            </p>
          </div>
        ) : (
          <div className="bg-gray-900 rounded-xl border border-gray-700 overflow-hidden">
            <div className="divide-y divide-gray-700">
              {approvals.map((req) => (
                <div key={req.id} className="px-6 py-5 hover:bg-gray-800 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-white">Version {req.version}</span>
                        <span className="text-gray-500 text-sm">→</span>
                        <div className="flex items-center gap-1.5">
                          {stageBadge(req.from_stage)}
                          <span className="text-gray-500">→</span>
                          {stageBadge(req.to_stage)}
                        </div>
                        {statusBadge(req.status)}
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-400">
                        <span>Requested by: <span className="text-gray-300">{req.requested_by}</span></span>
                        <span>
                          <ClockIcon className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                          {req.created_at ? new Date(req.created_at).toLocaleString() : '-'}
                        </span>
                      </div>
                      {req.comment && (
                        <p className="mt-2 text-sm text-gray-300 italic">"{req.comment}"</p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => openReview(req.id, 'approve')}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 text-green-400 text-xs font-medium rounded-lg transition-colors"
                      >
                        <CheckCircleIcon className="w-4 h-4" />
                        Approve
                      </button>
                      <button
                        onClick={() => openReview(req.id, 'reject')}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs font-medium rounded-lg transition-colors"
                      >
                        <XCircleIcon className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Approvals;
